import React, { useMemo, useState } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import { Alert, Box, Button, Card, CardContent, FormControl, MenuItem, Select, Tab, Tabs, Typography } from "@mui/material";
import DotPlot from "./DotPlot";
import BasePlot from "./BasePlot";

const Plot = createPlotlyComponent(Plotly);

type Props = { backendResult: any; legacyRows: any[] };
type DirectionFilter = "combined" | "up" | "down";

const candidateDirection = (row: any): "up" | "down" | "unknown" => {
  const label = String(row?.direction ?? "").toLowerCase();
  if (label.includes("down") || label.includes("lower")) return "down";
  if (label.includes("up") || label.includes("higher")) return "up";

  const logFoldChange = Number(
    row?.log_fold_change ?? row?.log2_fold_change ?? row?.logfoldchanges,
  );
  if (Number.isFinite(logFoldChange) && logFoldChange < 0) return "down";
  if (Number.isFinite(logFoldChange) && logFoldChange > 0) return "up";
  return "unknown";
};

const csvValue = (value: any) => {
  const text = value == null ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

const downloadText = (name: string, content: string, type: string) => {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
};

const downloadCsv = (name: string, rows: any[]) => {
  if (!rows.length) return;
  const columns = Array.from(new Set(rows.flatMap((row) => Object.keys(row))));
  const content = [columns.map(csvValue).join(","), ...rows.map((row) => columns.map((column) => csvValue(row[column])).join(","))].join("\n");
  downloadText(name, content, "text/csv;charset=utf-8");
};

const ResultTable = ({ rows }: { rows: any[] }) => {
  if (!rows.length) return <Typography color="text.secondary">No results available.</Typography>;
  const columns = Object.keys(rows[0]);
  return (
    <Box sx={{ overflow: "auto", maxHeight: 440, border: "1px solid #e5e7eb", borderRadius: 2 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 1 }}>
          <tr>{columns.map((column) => <th key={column} style={{ padding: 8, textAlign: "left", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" }}>{column.replace(/_/g, " ")}</th>)}</tr>
        </thead>
        <tbody>{rows.map((row, index) => <tr key={`${row.gene ?? "row"}-${index}`}>{columns.map((column) => <td key={column} style={{ padding: 8, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{typeof row[column] === "number" ? Number(row[column]).toPrecision(4) : String(row[column] ?? "")}</td>)}</tr>)}</tbody>
      </table>
    </Box>
  );
};

const BiomarkerDiscoveryResults = ({ backendResult, legacyRows }: Props) => {
  const [tab, setTab] = useState(0);
  const [ranking, setRanking] = useState("combined_rank");
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>("combined");
  const differentialMarkers = Array.isArray(backendResult?.differentialMarkers) ? backendResult.differentialMarkers : [];
  const candidates = useMemo(
    () => Array.isArray(backendResult?.candidateBiomarkers) ? backendResult.candidateBiomarkers : [],
    [backendResult?.candidateBiomarkers],
  );
  const filteredCandidates = useMemo(
    () => directionFilter === "combined"
      ? candidates
      : candidates.filter((row: any) => candidateDirection(row) === directionFilter),
    [candidates, directionFilter],
  );
  const metrics = Array.isArray(backendResult?.modelMetrics) ? backendResult.modelMetrics : [];
  const analysis = backendResult?.biomarkerAnalysis ?? {};
  const provenance = backendResult?.provenance ?? {};
  const warnings = Array.isArray(analysis.warnings) ? analysis.warnings : [];
  const isLegacy = !backendResult?.biomarkerAnalysis;

  const topCandidates = useMemo(() => {
    const direction = ranking === "combined_rank" ? 1 : -1;
    return filteredCandidates.slice().sort((a: any, b: any) => direction * (Number(a[ranking] ?? 0) - Number(b[ranking] ?? 0))).slice(0, 15);
  }, [filteredCandidates, ranking]);

  const rankingLabel = ranking === "combined_rank" ? "Combined rank score" : ranking === "random_forest_importance" ? "Random Forest importance" : "Logistic |coefficient|";
  const chartValues = topCandidates.map((row: any) =>
    ranking === "combined_rank"
      ? 1 / Math.max(1, Number(row.combined_rank ?? 1))
      : ranking === "abs_logistic_coefficient"
        ? Number(row.abs_logistic_coefficient ?? 0)
        : Number(row[ranking] ?? 0),
  );
  const chartAxisLabel = ranking === "combined_rank"
    ? "Combined evidence score (1/rank; higher is stronger)"
    : `${rankingLabel} (higher is stronger)`;
  const chartText = topCandidates.map((row: any, index: number) =>
    ranking === "combined_rank"
      ? `Rank #${Number(row.combined_rank ?? index + 1)}`
      : chartValues[index].toPrecision(3),
  );
  const metricTraces: any[] = metrics.flatMap((metric: any) =>
    ["auroc", "f1", "precision", "recall"].map((name) => ({
      x: [name.toUpperCase()], y: [metric[name]], name: metric.model, type: "bar",
    })),
  );

  const downloadJson = () => downloadText("biomarker_analysis.json", JSON.stringify({ comparison: analysis.comparison ?? {}, candidate_gene_filters: analysis.candidateGeneFilters ?? {}, validation_strategy: analysis.validationStrategy ?? {}, logistic_regression: metrics.find((item: any) => item.model === "Logistic Regression") ?? {}, random_forest: metrics.find((item: any) => item.model === "Random Forest") ?? {}, top_biomarkers: candidates.slice(0, 20), provenance }, null, 2), "application/json");

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <Tabs value={tab} onChange={(_event, value) => setTab(value)} variant="scrollable" allowScrollButtonsMobile>
        <Tab label="Overview" />
        <Tab label="Differential Markers" />
        <Tab label="Candidate Biomarkers" />
      </Tabs>

      {tab === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {isLegacy && <Alert severity="info">This saved result contains differential markers only. Biomarker modelling was not part of that run.</Alert>}
          {warnings.map((warning: string) => <Alert severity="warning" key={warning}>{warning}</Alert>)}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 2 }}>
            {[
              ["Comparison", analysis?.comparison?.grouping ? `${analysis.comparison.grouping} clusters` : "Cluster markers"],
              ["DE genes", differentialMarkers.length || legacyRows.length],
              ["Genes used for ML", analysis?.candidateGeneCount ?? 0],
              ["Top candidate", candidates[0]?.gene ?? "Not available"],
              ["Validation", analysis?.validationStrategy?.method ? `${analysis.validationStrategy.method} (${analysis.validationStrategy.folds} folds)` : "Not run"],
            ].map(([label, value]) => <Card variant="outlined" key={String(label)}><CardContent><Typography variant="caption" color="text.secondary">{label}</Typography><Typography variant="h6" sx={{ mt: 0.5 }}>{String(value)}</Typography></CardContent></Card>)}
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 2 }}>
            {metrics.map((metric: any) => <Card variant="outlined" key={metric.model}><CardContent><Typography variant="subtitle1" fontWeight={700}>{metric.model}</Typography><Typography variant="h5" color="primary">AUROC {Number(metric.auroc).toFixed(3)}</Typography><Typography variant="body2">F1 {Number(metric.f1).toFixed(3)} · Precision {Number(metric.precision).toFixed(3)} · Recall {Number(metric.recall).toFixed(3)}</Typography></CardContent></Card>)}
          </Box>
          {!!metrics.length && (
            <BasePlot title="Cross-validated model performance" plotType="custom" downloadFilename="biomarker_model_performance">
              <Plot
                data={metricTraces}
                layout={{ barmode: "group", autosize: true, margin: { l: 55, r: 20, t: 25, b: 55 }, yaxis: { title: "Score", range: [0, 1] }, paper_bgcolor: "white", plot_bgcolor: "white" }}
                config={{ responsive: true, displaylogo: false }}
                style={{ width: "100%", height: "100%" }}
                className="plotly-plot"
                useResizeHandler={true}
              />
            </BasePlot>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <DotPlot data={legacyRows} title="Top Differentially Expressed Genes Across Clusters" />
          <Box><Button onClick={() => downloadCsv("differential_markers.csv", differentialMarkers)} disabled={!differentialMarkers.length}>Download differential markers CSV</Button></Box>
          <ResultTable rows={differentialMarkers.slice(0, 500)} />
        </Box>
      )}

      {tab === 2 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {!candidates.length ? <Alert severity="info">Candidate biomarker modelling is unavailable for this run. Differential marker results remain available.</Alert> : <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography fontWeight={600}>Rank genes by</Typography>
              <FormControl size="small" sx={{ minWidth: 230 }}><Select value={ranking} onChange={(event) => setRanking(event.target.value)}><MenuItem value="combined_rank">Combined ranking</MenuItem><MenuItem value="random_forest_importance">Random Forest importance</MenuItem><MenuItem value="abs_logistic_coefficient">Logistic Regression |coefficient|</MenuItem></Select></FormControl>
              <Typography fontWeight={600}>Direction</Typography>
              <FormControl size="small" sx={{ minWidth: 180 }}><Select value={directionFilter} onChange={(event) => setDirectionFilter(event.target.value as DirectionFilter)}><MenuItem value="combined">Combined</MenuItem><MenuItem value="up">Upregulated only</MenuItem><MenuItem value="down">Downregulated only</MenuItem></Select></FormControl>
              <Typography variant="body2" color="text.secondary">{filteredCandidates.length} genes</Typography>
            </Box>
            <Alert severity="info" icon={false}>
              Strongest candidates are shown at the top. Bar length is the selected evidence score; red means upregulated and blue means downregulated.
            </Alert>
            <BasePlot title={`Top Candidate Biomarkers — ${rankingLabel}`} plotType="custom" downloadFilename="top_candidate_biomarkers">
              <Plot
                data={[{
                  type: "bar",
                  orientation: "h",
                  x: chartValues.slice().reverse(),
                  y: topCandidates.map((row: any) => row.gene).reverse(),
                  marker: {
                    color: topCandidates.map((row: any) => candidateDirection(row) === "down" ? "#2563eb" : candidateDirection(row) === "up" ? "#dc2626" : "#64748b").reverse(),
                  },
                  text: chartText.slice().reverse(),
                  textposition: "auto",
                  customdata: topCandidates.map((row: any) => [row.direction, row.combined_rank, row.log_fold_change]).reverse(),
                  hovertemplate: "%{y}<br>Score: %{x:.4g}<br>%{customdata[0]}<br>Combined rank: %{customdata[1]}<br>logFC: %{customdata[2]:.3g}<extra></extra>",
                } as any]}
                layout={{
                  autosize: true,
                  margin: { l: 120, r: 35, t: 25, b: 70 },
                  xaxis: { title: chartAxisLabel, rangemode: "tozero", automargin: true },
                  yaxis: { automargin: true },
                  paper_bgcolor: "white",
                  plot_bgcolor: "white",
                }}
                config={{ responsive: true, displaylogo: false }}
                style={{ width: "100%", height: "100%" }}
                className="plotly-plot"
                useResizeHandler={true}
              />
            </BasePlot>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}><Button onClick={() => downloadCsv(`candidate_biomarkers_${directionFilter}.csv`, filteredCandidates)} disabled={!filteredCandidates.length}>Candidate biomarkers CSV</Button><Button onClick={() => downloadCsv("biomarker_model_metrics.csv", metrics)}>Model metrics CSV</Button><Button onClick={downloadJson}>Analysis JSON</Button></Box>
            <ResultTable rows={filteredCandidates} />
          </>}
        </Box>
      )}
    </Box>
  );
};

export default BiomarkerDiscoveryResults;
